import { RecurrenceAnchor, RecurrenceDay, RecurrenceRule } from '@aslanpay/portal-common-lib';

/**
 * The parts of an anchor the form does not ask about.
 * <p>
 * Naming no day leaves the cycle on the day its own schedule already puts it on, which is why the form does
 * not ask. A rule built elsewhere may still name one, so whatever it named is carried back untouched rather
 * than dropped by an edit that never mentioned it.
 */
type PreservedAnchor = Omit<RecurrenceAnchor, 'type' | 'frequency' | 'interval'>;

/** The recurrence as the form holds it, before it becomes a rule the service understands. */
export interface RecurrenceFormState {
  recurring: boolean;
  frequency: RecurrenceRule['anchor']['frequency'];
  interval: string;
  preserved: PreservedAnchor;
  endsType: RecurrenceRule['ends']['type'];
  endsOnDate: string;
  endsAfterCount: string;
}

/** Used to read a rule back in words. The form does not offer a day, since the schedule already fixes it. */
export const DAYS: { value: RecurrenceDay; label: string }[] = [
  { value: 'MONDAY', label: 'Monday' },
  { value: 'TUESDAY', label: 'Tuesday' },
  { value: 'WEDNESDAY', label: 'Wednesday' },
  { value: 'THURSDAY', label: 'Thursday' },
  { value: 'FRIDAY', label: 'Friday' },
  { value: 'SATURDAY', label: 'Saturday' },
  { value: 'SUNDAY', label: 'Sunday' },
];

/** -1 is the last such weekday in the month, which is how a calendar app puts it. */
export const WEEKS: { value: string; label: string }[] = [
  { value: '1', label: 'first' },
  { value: '2', label: 'second' },
  { value: '3', label: 'third' },
  { value: '4', label: 'fourth' },
  { value: '-1', label: 'last' },
];

export const emptyRecurrence: RecurrenceFormState = {
  recurring: false,
  frequency: 'MONTHLY',
  interval: '1',
  preserved: {},
  endsType: 'NEVER',
  endsOnDate: '',
  endsAfterCount: '12',
};

/**
 * The rule as the service reads it, or undefined where the campaign does not recur.
 * <p>
 * How often, and nothing about which day: the cycle keeps the day its own schedule puts it on. Anything a
 * rule built elsewhere named is carried through rather than quietly unset.
 */
export function toRecurrenceRule(form: RecurrenceFormState): RecurrenceRule | undefined {
  if (!form.recurring) return undefined;

  const anchor: RecurrenceRule['anchor'] = {
    ...form.preserved,
    type: 'CALENDAR_RULE',
    frequency: form.frequency,
    interval: Number(form.interval) || 1,
  };

  return {
    anchor,
    ends: {
      type: form.endsType,
      onDate: form.endsType === 'ON_DATE' ? form.endsOnDate : undefined,
      count: form.endsType === 'AFTER_COUNT' ? Number(form.endsAfterCount) : undefined,
    },
  };
}

/** What is wrong with the recurrence, as a sentence, or nothing where it is sound. */
export function recurrenceError(form: RecurrenceFormState): string | undefined {
  if (!form.recurring) return undefined;

  const interval = Number(form.interval);
  if (!Number.isInteger(interval) || interval < 1) {
    return 'Repeat every must be a whole number of at least one.';
  }

  if (form.endsType === 'ON_DATE' && !form.endsOnDate) {
    return 'Set the date the campaign stops repeating.';
  }

  if (form.endsType === 'AFTER_COUNT') {
    const count = Number(form.endsAfterCount);
    if (!Number.isInteger(count) || count < 1) {
      return 'The number of times it runs must be a whole number of at least one.';
    }
  }

  return undefined;
}

/** A rule read back from the service, as the form holds it. */
export function fromRecurrenceRule(
  recurring: boolean,
  rule: RecurrenceRule | null | undefined
): RecurrenceFormState {
  if (!recurring || !rule) return emptyRecurrence;

  const { anchor, ends } = rule;
  const { frequency, interval } = anchor;
  const preserved: PreservedAnchor = {
    byDay: anchor.byDay,
    byWeek: anchor.byWeek,
    bySetPos: anchor.bySetPos,
    byMonthDay: anchor.byMonthDay,
    byMonth: anchor.byMonth,
    offsetDays: anchor.offsetDays,
  };

  return {
    recurring: true,
    frequency,
    interval: String(interval ?? 1),
    preserved,
    endsType: ends?.type ?? 'NEVER',
    endsOnDate: ends?.onDate ?? '',
    endsAfterCount: String(ends?.count ?? 12),
  };
}

/** The rule in plain words, for reading rather than editing. */
export function describeRecurrence(
  recurring: boolean,
  rule: RecurrenceRule | null | undefined
): string {
  if (!recurring) return 'Runs once';
  if (!rule) return 'Repeats';

  const { anchor, ends } = rule;
  const every = (anchor.interval ?? 1) > 1 ? `every ${anchor.interval} ` : 'every ';
  const dayName = DAYS.find((day) => day.value === anchor.byDay?.[0])?.label ?? '';

  let when = '';
  if (anchor.frequency === 'WEEKLY') {
    when = `${every}${(anchor.interval ?? 1) > 1 ? 'weeks' : 'week'} on ${dayName}`;
  } else if (anchor.frequency === 'MONTHLY' && anchor.byWeek) {
    const week = WEEKS.find((option) => option.value === String(anchor.byWeek))?.label ?? '';
    when = `${every}${(anchor.interval ?? 1) > 1 ? 'months' : 'month'} on the ${week} ${dayName}`;
  } else if (anchor.frequency === 'MONTHLY') {
    when = `${every}${(anchor.interval ?? 1) > 1 ? 'months' : 'month'} on day ${
      anchor.byMonthDay ?? 1
    }`;
  } else {
    when = `${every}${(anchor.interval ?? 1) > 1 ? 'years' : 'year'}`;
  }

  if (ends?.type === 'ON_DATE' && ends.onDate) return `Repeats ${when}, until ${ends.onDate}`;
  if (ends?.type === 'AFTER_COUNT' && ends.count) return `Repeats ${when}, ${ends.count} times`;

  return `Repeats ${when}`;
}
